import { Avatar, Box, Stack, Tooltip, Typography } from '@mui/material';
import React from 'react';

import {
  avatarColors,
  AvatarPerson,
  personFullName,
  personInitials,
} from '@/components/ui/userAvatar/userAvatar.util.ts';
import { themeColors } from '@/config/theme/themeColors.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

type Props = {
  person?: AvatarPerson;
  role?: string;
  size?: number;
  tooltip?: string;
  withName?: boolean;
};

const DEFAULT_SIZE = 26;

const useIsCurrentUser = (person?: AvatarPerson): boolean => {
  const user = useUserContext();
  if (!person) {
    return false;
  }
  if (person.userId) {
    return person.userId === user.userId;
  }
  return person.firstName === user.firstName && person.lastName === user.lastName;
};

export const UserAvatar: React.FC<Props> = ({
  person,
  role,
  size = DEFAULT_SIZE,
  tooltip,
  withName,
}) => {
  const { t } = useTranslationWithPrefix('components.listDetails.avatar');
  const isCurrentUser = useIsCurrentUser(person);

  if (!person) {
    return <Typography sx={{ color: themeColors.grey }}>—</Typography>;
  }

  const fullName = personFullName(person);
  const colors = avatarColors(fullName);
  const nameWithYou = isCurrentUser ? `${fullName} (${t('you')})` : fullName;

  const avatar = (
    <Avatar
      sx={{
        backgroundColor: colors.background,
        border: 'none',
        boxShadow: isCurrentUser
          ? `0 0 0 2px ${themeColors.white}, 0 0 0 3px ${themeColors.accentColor}`
          : 'none',
        color: colors.color,
        fontSize: Math.round(size * 0.42),
        fontWeight: 600,
        height: size,
        letterSpacing: 0.2,
        lineHeight: 1,
        width: size,
      }}
    >
      {personInitials(person)}
    </Avatar>
  );

  if (!withName) {
    return (
      <Tooltip title={tooltip ?? (role ? `${role}: ${nameWithYou}` : nameWithYou)}>
        <span style={{ display: 'inline-flex' }}>{avatar}</span>
      </Tooltip>
    );
  }

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
      {avatar}
      <Typography noWrap sx={{ color: themeColors.greyDarker }} variant="f14Regular">
        {nameWithYou}
      </Typography>
    </Stack>
  );
};

type GroupProps = {
  max?: number;
  persons: AvatarPerson[];
  role?: string;
  size?: number;
};

export const UserAvatarGroup: React.FC<GroupProps> = ({
  max = 5,
  persons,
  role,
  size = DEFAULT_SIZE,
}) => {
  if (persons.length === 0) {
    return <Typography sx={{ color: themeColors.grey }}>—</Typography>;
  }

  const shown = persons.length > max ? persons.slice(0, max - 1) : persons;
  const hidden = persons.slice(shown.length);

  return (
    <Stack direction="row" sx={{ alignItems: 'center' }}>
      {shown.map((person, index) => (
        <Box
          key={person.userId ?? personFullName(person)}
          sx={{
            borderRadius: '50%',
            boxShadow: `0 0 0 2px ${themeColors.white}`,
            display: 'inline-flex',
            ml: index === 0 ? 0 : -0.75,
          }}
        >
          <UserAvatar person={person} role={role} size={size} />
        </Box>
      ))}
      {hidden.length > 0 && (
        <Tooltip title={hidden.map(personFullName).join(', ')}>
          <Avatar
            sx={{
              backgroundColor: themeColors.stroke,
              border: 'none',
              boxShadow: `0 0 0 2px ${themeColors.white}`,
              color: themeColors.greyDark,
              fontSize: Math.round(size * 0.4),
              height: size,
              ml: -0.75,
              width: size,
            }}
          >
            +{hidden.length}
          </Avatar>
        </Tooltip>
      )}
    </Stack>
  );
};
